import React from 'react';
import {ViewColumn} from '../../../../../components/initial-plan/pdf/layout/PDFLayout';
import {TextHeader, styles, ViewRow} from './InvoicePDFLayout';
import {Text, Image} from '@react-pdf/renderer';
import {companyLogoSrc} from '../../../../../directApi';
import Util from '../../../../../forecast-app/shared/util/util';
import {IntlShape} from 'react-intl';
import {isoCountries} from '../../../../../constants';
import type {InvoicePDFDownloadModal_viewer$data as InvoicePDFDownloadModal_viewer} from './__generated__/InvoicePDFDownloadModal_viewer.graphql';
import {OptionalInvoiceExportFieldStates} from './InvoicePDFTypes';
import {composeClientContactName} from '../../../../../forecast-app/shared/util/clientContactName';

type InvoicePDFHeaderProps = {
	invoiceData: InvoicePDFDownloadModal_viewer['invoice'];
	companyData: InvoicePDFDownloadModal_viewer['company'];
	optionalFields: OptionalInvoiceExportFieldStates;
	intl: IntlShape;
};

const InvoicePDFHeader = ({invoiceData, companyData, optionalFields, intl}: InvoicePDFHeaderProps): JSX.Element => {
	const logoSrc = companyData?.logoId !== null ? companyLogoSrc(companyData?.logoId) : null;

	// get projects
	const projectNamesArray = invoiceData?.entries?.edges?.map(entry => entry?.node?.project?.name);
	const projectNames = [...new Set(projectNamesArray)];

	const contactName = composeClientContactName(invoiceData?.clientContact);

	const dueDate = Util.CreateNonUtcMomentDate(invoiceData?.dueYear, invoiceData?.dueMonth, invoiceData?.dueDay);
	const dueDateString = `${intl.formatDate(dueDate, {day: 'numeric', month: 'long'})}, ${intl.formatDate(dueDate, {
		year: 'numeric',
	})}`;

	const invoiceDate = Util.CreateNonUtcMomentDate(
		invoiceData?.createdYear,
		invoiceData?.createdMonth,
		invoiceData?.createdDay
	);
	const invoiceDateString = `${intl.formatDate(invoiceDate, {day: 'numeric', month: 'long'})}, ${intl.formatDate(
		invoiceDate,
		{
			year: 'numeric',
		}
	)}`;

	const capitalizeAllWords = (str: string) => {
		return str
			.split(' ')
			.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(' ');
	};

	return (
		<ViewRow style={{justifyContent: 'space-between', paddingBottom: '8px'}}>
			<ViewColumn style={{width: '55%'}}>
				{optionalFields.companyLogoChecked && logoSrc ? (
					<Image
						src={{uri: logoSrc, credentials: 'include'}}
						style={{
							width: '200px',
							height: '140px',
							objectFit: 'contain',
							objectPosition: 'top left',
							paddingBottom: '29px',
						}}
					/>
				) : (
					<Text style={{height: '140px'}}>&nbsp;</Text>
				)}
				{optionalFields.invoiceIdChecked && (
					<Text style={styles.textMain12}>
						<Text style={styles.textMainBold}>{intl.formatMessage({id: 'common.invoice_id'})}:</Text> I-
						{invoiceData?.companyInvoiceId}
					</Text>
				)}
				{optionalFields.invoiceReferenceChecked && (
					<Text style={styles.textMain12}>
						<Text style={styles.textMainBold}>
							{capitalizeAllWords(intl.formatMessage({id: 'project_section.invoicing_invoice_reference'}))}:
						</Text>{' '}
						{invoiceData?.invoiceReference}
					</Text>
				)}
				{optionalFields.invoiceNameChecked && (
					<Text style={styles.textMain12}>
						<Text style={styles.textMainBold}>
							{capitalizeAllWords(intl.formatMessage({id: 'invoice.pdf_optional_info.invoice_name'}))}:
						</Text>{' '}
						{invoiceData?.name}
					</Text>
				)}
				<Text style={styles.textMain12}>
					<Text style={styles.textMainBold}>
						{capitalizeAllWords(intl.formatMessage({id: 'invoicing.invoice_date'}))}:
					</Text>{' '}
					{invoiceDateString}
				</Text>
				<Text style={styles.textMain12}>
					<Text style={styles.textMainBold}>{intl.formatMessage({id: 'common.due_date'})}:</Text> {dueDateString}
				</Text>
				{projectNames.length > 0 && (
					<Text style={styles.textMain12}>
						<Text style={styles.textMainBold}>Subject: </Text>
						{projectNames.join(', ')}
					</Text>
				)}
			</ViewColumn>

			<ViewColumn style={{justifyContent: 'flex-start'}}>
				<ViewRow>
					<TextHeader style={{paddingBottom: '29px'}}>INVOICE</TextHeader>
				</ViewRow>
				{optionalFields.companyAddressChecked && (
					<ViewRow style={{justifyContent: 'flex-start', paddingBottom: '29px'}}>
						<ViewColumn style={{marginLeft: '-42px', justifyContent: 'flex-start', width: '42px'}}>
							<Text style={styles.fromToText}>{intl.formatMessage({id: 'common.from'})}:</Text>
						</ViewColumn>
						<ViewColumn style={{justifyContent: 'flex-start'}}>
							<Text style={[styles.textMainBold, {paddingBottom: '9px'}]}>{companyData?.name}</Text>
							{companyData?.street && (
								<Text style={[styles.textMain, {maxWidth: '200px'}]}>{companyData?.street}</Text>
							)}
							{companyData?.zip || companyData?.city ? (
								<Text style={[styles.textMain, {maxWidth: '200px'}]}>
									{companyData?.zip ? `${companyData?.zip} ` : ''}
									{companyData?.city}
								</Text>
							) : null}
							{companyData?.country && (
								<Text style={styles.textMain}>
									{isoCountries.find(c => c.value === companyData?.country)?.label}
								</Text>
							)}
							{companyData?.vatId && optionalFields.companyVatChecked && (
								<Text style={[styles.textMain, {marginTop: '8px'}]}>VAT Number: {companyData?.vatId}</Text>
							)}
						</ViewColumn>
					</ViewRow>
				)}
				{optionalFields.clientAddressChecked && (
					<ViewRow style={{justifyContent: 'flex-start'}}>
						<ViewColumn style={{marginLeft: '-28px', justifyContent: 'flex-start', width: '28px'}}>
							<Text style={styles.fromToText}>{intl.formatMessage({id: 'common.to'})}:</Text>
						</ViewColumn>
						<ViewColumn style={{justifyContent: 'flex-start'}}>
							<Text style={[styles.textMainBold, {paddingBottom: '9px'}]}>
								{optionalFields.contactNameChecked && contactName && invoiceData?.client?.name
									? `${contactName} - ${invoiceData?.client?.name}`
									: invoiceData?.client?.name}
							</Text>
							{invoiceData?.client?.street && (
								<Text style={[styles.textMain, {maxWidth: '200px'}]}>{invoiceData?.client?.street}</Text>
							)}
							{invoiceData?.client?.zip || invoiceData?.client?.city ? (
								<Text style={[styles.textMain, {maxWidth: '200px'}]}>
									{invoiceData?.client?.zip ? `${invoiceData?.client?.zip} ` : ''}
									{invoiceData?.client?.city}
								</Text>
							) : null}
							{invoiceData?.client?.region && (
								<Text style={[styles.textMain, {maxWidth: '200px'}]}>{invoiceData?.client?.region}</Text>
							)}
							{invoiceData?.client?.country && (
								<Text style={styles.textMain}>
									{isoCountries.find(c => c.value === invoiceData?.client?.country)?.label}
								</Text>
							)}
						</ViewColumn>
					</ViewRow>
				)}
			</ViewColumn>
		</ViewRow>
	);
};

export default InvoicePDFHeader;
